import API from "../../services/api";
import {
  createIdempotencyKey,
  getApiErrorDetail,
  idempotencyHeaders,
} from "../../utils/idempotency";

export { createIdempotencyKey };

/** @deprecated Use createIdempotencyKey — kept for existing payroll screens. */
export function newIdempotencyKey() {
  return createIdempotencyKey("payroll");
}

// PAY PERIOD
export const createPayPeriod = (data) =>
  API.post("/payrolls/pay-periods", data);

export const listPayPeriods = () => API.get("/payrolls/pay-periods");

export const deletePayPeriod = (payPeriodId) =>
  API.delete(`/payrolls/pay-periods/${payPeriodId}`);

export const fetchPayrollHub = () => API.get("/payrolls/hub");

export const fetchPayrollRunDetail = (payrollRunId) =>
  API.get(`/payrolls/${payrollRunId}/detail`);

export const fetchPayrollSalaryPackageCheck = (payrollRunId) =>
  API.get(`/payrolls/${payrollRunId}/salary-package-check`);

// PAYROLL RUN
export const createPayrollRun = (data) => API.post("/payrolls", data);

/**
 * Use the payroll run that already belongs to this Payroll ID.
 * Creates one only when the Payroll ID has no run yet.
 */
export async function ensurePayrollRunForPeriod({ organisationId, payPeriodId }) {
  const findExisting = async () => {
    const hub = await fetchPayrollHub();
    const period = (hub.data?.periods || []).find(
      (row) => String(row.pay_period_id) === String(payPeriodId)
    );
    return (period?.runs || []).find((run) => run?.payroll_run_id) || null;
  };

  const existing = await findExisting();
  if (existing) return existing;

  try {
    const { data } = await createPayrollRun({
      organisation_id: organisationId,
      pay_period_id: payPeriodId,
    });
    return data;
  } catch (error) {
    const again = await findExisting();
    if (again) return again;
    throw error;
  }
}

export const getPayroll = (id) => API.get(`/payrolls/${id}`);

/**
 * GET /payrolls/:id — poll this for execution_status (same as backend; no separate /status route).
 */
export const getPayrollRunStatus = (id) => API.get(`/payrolls/${id}`);

// ORG SETTINGS (attendance payroll defaults)
export const enableAttendancePayrollDefaults = () =>
  API.post("/organisation/me/hr-settings/enable-attendance-payroll");

/**
 * POST /payrolls/:id/process — accepts 200 (sync) or 202 (Celery).
 * @param {string} runId
 * @param {{ idempotencyKey?: string, shadowLegacy?: boolean, parallelism?: number }} options
 */
export const processPayroll = (runId, options = {}) => {
  const idempotencyKey = options.idempotencyKey ?? newIdempotencyKey();
  const params = {};
  if (options.shadowLegacy === true) params.shadow_legacy = true;
  if (options.parallelism != null && options.parallelism !== "") {
    params.parallelism = options.parallelism;
  }
  return API.post(`/payrolls/${runId}/process`, {}, {
    headers: { "Idempotency-Key": idempotencyKey },
    params,
    validateStatus: (s) => s === 200 || s === 202,
  }).then((res) => ({ ...res, idempotencyKey }));
};

export const getExecutionTrace = (runId) =>
  API.get(`/payrolls/${runId}/execution-trace`);

export const replayVerifyPayroll = (runId, options = {}) => {
  const idempotencyKey = options.idempotencyKey ?? newIdempotencyKey();
  return API.post(
    `/payrolls/${runId}/replay-verify`,
    {},
    {
      headers: { "Idempotency-Key": idempotencyKey },
    }
  ).then((res) => ({ ...res, idempotencyKey }));
};

/**
 * POST /payrolls/batch/process
 * @param {string[]} payrollRunIds
 * @param {{ idempotencyKey?: string, shadowLegacy?: boolean, parallelism?: number }} options
 */
export const batchProcessPayrolls = (payrollRunIds, options = {}) => {
  const idempotencyKey = options.idempotencyKey ?? newIdempotencyKey();
  const params = {};
  if (options.shadowLegacy === true) params.shadow_legacy = true;
  if (options.parallelism != null && options.parallelism !== "") {
    params.parallelism = options.parallelism;
  }
  return API.post(
    "/payrolls/batch/process",
    { payroll_run_ids: payrollRunIds },
    {
      headers: { "Idempotency-Key": idempotencyKey },
      params,
      validateStatus: (s) => s === 200 || s === 202,
    }
  ).then((res) => ({ ...res, idempotencyKey }));
};

// SUMMARY
export const getPayrollSummary = (id) =>
  API.get(`/payrolls/${id}/summary`);

// REPORTS
export const getPayrollRegister = (id, { live = true } = {}) =>
  API.get(`/payrolls/${id}/register`, { params: { live } });

export const getSalaryStatement = (id) =>
  API.get(`/payrolls/${id}/salary-statement`);

export const getTdsSummary = (id) =>
  API.get(`/payrolls/${id}/tds-summary`);

export const getBankTransferReport = (id) =>
  API.get(`/payrolls/${id}/bank-transfer-report`);

export const downloadPayslip = (runId, empId, { live = true } = {}) =>
  API.get(`/payslips/${runId}/${empId}/download`, {
    params: { live },
    responseType: "blob",
  });

/** Phase 4: JSON payslip from stored entries (locked payroll only). */
export const getPayslipData = (employeeId, payrollRunId, { live = true } = {}) =>
  API.get(`/payslip/${employeeId}`, {
    params: { payroll_run_id: payrollRunId, live },
  });

export const verifyPayrollLifecycle = (runId) =>
  API.post(`/payrolls/${runId}/lifecycle/verify`);

export const approvePayrollLifecycle = (runId) =>
  API.post(`/payrolls/${runId}/lifecycle/approve`);

export const lockPayrollLifecycle = (runId) =>
  API.post(`/payrolls/${runId}/lifecycle/lock`);

export const getPayrollLifecycleAudit = (runId) =>
  API.get(`/payrolls/${runId}/lifecycle/audit`);


const ERROR_SEVERITY_CODES = new Set([
  "PAYROLL_SALARY_ENGINE_ERROR",
  "PAYROLL_TEMPLATE_CYCLE",
  "PAYROLL_TEMPLATE_INVALID",
  "PAYROLL_STATUTORY_SETUP",
  "PAYROLL_SETUP_CONFLICT",
  "PAYROLL_DATA_CONFLICT",
  "PAYROLL_PROCESS_FAILED",
  "PAYROLL_VALIDATION_ERROR",
  "ATTENDANCE_VALIDATION_FAILED",
  "PAYROLL_MISSING_SALARY_PACKAGES",
  "PAYROLL_LIFECYCLE_LOCKED",
  "INTERNAL_ERROR",
]);

function noticeVariant(code, severity) {
  if (severity === "error" || severity === "warning" || severity === "info" || severity === "success") {
    return severity;
  }
  if (ERROR_SEVERITY_CODES.has(code)) return "error";
  if (code === "PAYROLL_DATABASE_ERROR" || code === "PAYROLL_MODE_MISMATCH" || code === "PAYROLL_ALREADY_PROCESSED") {
    return "warning";
  }
  if (code === "PAYROLL_EXECUTION_IN_PROGRESS") return "info";
  return "error";
}

/** Client-side fallback when API returns a raw salary-engine string. */
function humanizeSalaryEngineText(text) {
  const raw = String(text || "");
  const cleaned = raw
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "an employee")
    .replace(/^Salary engine errors for employee\s+[^:]+:\s*/i, "")
    .replace(/\(template\s*=\s*[^)]+\)/gi, "")
    .replace(/\s+/g, " ")
    .trim();

  const missing = cleaned.match(
    /(?:Derived variable\s+'?([A-Z0-9_]+)'?\s+is not defined|undefined derived variable\s+'?([A-Z0-9_]+)'?)/i
  );
  if (missing) {
    const code = (missing[1] || missing[2] || "").toUpperCase();
    const isSpecial = code === "SPECIAL_ALLOWANCE";
    return {
      code: "PAYROLL_SALARY_ENGINE_ERROR",
      title: `Missing formula: ${code.replace(/_/g, " ")}`,
      message: `Payroll could not calculate this component because the formula “${code}” is not set up for your organisation yet.`,
      hint: "Add the formula under Salary Engine → Derived Variables, then process payroll again.",
      steps: [
        "Open Salary Engine → Derived Variables (Formulas)",
        isSpecial
          ? "Add SPECIAL_ALLOWANCE — typically: MONTHLY_CTC − (BASIC + HRA)"
          : `Add a derived variable named “${code}”`,
        "Confirm the salary template maps this component to that formula",
        "Return here and click Process Payroll again",
      ],
      actionType: "derived_variables",
      variant: "error",
    };
  }

  return null;
}

/**
 * Fallback guide links when backend does not send guideLinks (older API).
 * Paths match Payroll Setup tabs under /salary-v2.
 */
export function defaultPayrollGuideLinks(code, message = "") {
  const msg = String(message || "").toLowerCase();
  const settings = { label: "3.1 Payroll Settings", to: "/salary-v2/setup" };
  const components = { label: "3.2 Salary Components", to: "/salary-v2/components" };
  const structures = { label: "3.3 Salary Structures", to: "/salary-v2/structures" };
  const payGroups = { label: "3.4 Pay Groups", to: "/salary-v2/pay-groups" };
  const formulas = { label: "Derived Formulas", to: "/salary-v2/derived-variables" };
  const templates = { label: "Template Builder", to: "/salary/templates" };
  const assign = { label: "Assign to Employees", to: "/employeesalarystructure" };
  const groups = { label: "Component Groups", to: "/salary-v2/groups" };
  const statutory = { label: "Statutory Settings", to: "/salary-v2/statutory" };

  if (code === "PAYROLL_DERIVED_VARIABLE" || (msg.includes("derived variable") && msg.includes("not defined"))) {
    return [{ ...formulas, primary: true }, components, structures, templates, assign];
  }
  if (code === "PAYROLL_STATUTORY_SETUP") {
    return [{ ...statutory, primary: true }, components, groups, structures];
  }
  if (code === "PAYROLL_TEMPLATE_CYCLE") {
    return [{ ...formulas, primary: true }, structures, { label: "Formula Builder", to: "/salary-v2/formula-builder" }];
  }
  if (code === "PAYROLL_TEMPLATE_INVALID") {
    return [{ ...structures, primary: true }, templates, components, assign];
  }
  if (code === "PAYROLL_PAY_GROUP") {
    return [{ ...payGroups, primary: true }, settings];
  }
  if (code === "PAYROLL_SETUP_CONFLICT") {
    return [{ ...components, primary: true }, structures, groups];
  }
  if (
    code === "PAYROLL_SALARY_ENGINE_ERROR"
    || code === "PAYROLL_PROCESS_FAILED"
    || code === "PAYROLL_DATA_CONFLICT"
  ) {
    return [settings, components, formulas, { ...structures, primary: true }, templates, assign];
  }
  if (code === "PAYROLL_MISSING_SALARY_PACKAGES" || code === "salary_packages") {
    return [{ ...assign, primary: true }, structures];
  }
  return null;
}

/**
 * Structured payroll error for professional UI cards (title, steps, action hints).
 */
export function parsePayrollProcessError(error) {
  const detail = error?.response?.data?.detail;
  const fallback = {
    code: "UNKNOWN",
    title: "Payroll could not be processed",
    message: "Something went wrong. Please try again.",
    hint: null,
    steps: ["Review your payroll run and attendance data", "Try again in a moment"],
    actionType: null,
    variant: "error",
    guideLinks: null,
  };

  if (detail && typeof detail === "object" && detail.code) {
    const code = detail.code;
    const backendMsg = detail.message || "";
    const backendHint = detail.hint || null;
    const backendSteps = Array.isArray(detail.steps) ? detail.steps.filter(Boolean) : [];
    const actionType = detail.actionType || null;
    const variant = noticeVariant(code, detail.severity);
    const backendLinks = Array.isArray(detail.guideLinks) ? detail.guideLinks.filter((l) => l?.label && l?.to) : [];

    const titled = (title) => ({
      code,
      title: detail.title || title,
      message: backendMsg || fallback.message,
      hint: backendHint,
      steps: backendSteps.length ? backendSteps : fallback.steps,
      actionType,
      variant,
      guideLinks: backendLinks.length ? backendLinks : defaultPayrollGuideLinks(code, backendMsg),
    });

    if (
      backendMsg &&
      (backendSteps.length || backendHint || detail.title) &&
      !["PAYROLL_MODE_MISMATCH", "PAYROLL_ALREADY_PROCESSED", "ATTENDANCE_VALIDATION_FAILED",
        "PAYROLL_MISSING_SALARY_PACKAGES", "PAYROLL_LIFECYCLE_LOCKED", "PAYROLL_EXECUTION_IN_PROGRESS"].includes(code)
    ) {
      const titles = {
        PAYROLL_SETUP_CONFLICT: "Salary component setup conflict",
        PAYROLL_DATA_CONFLICT: "Payroll data conflict",
        PAYROLL_DATABASE_ERROR: "Temporary system issue",
        PAYROLL_SALARY_ENGINE_ERROR: "Salary calculation failed",
        PAYROLL_DERIVED_VARIABLE: "Missing formula / derived variable",
        PAYROLL_TEMPLATE_CYCLE: "Salary template configuration error",
        PAYROLL_TEMPLATE_INVALID: "Salary template needs correction",
        PAYROLL_STATUTORY_SETUP: "Statutory deductions not ready",
        PAYROLL_PAY_GROUP: "Pay group setup needed",
        PAYROLL_PROCESS_FAILED: "Payroll could not be processed",
        PAYROLL_VALIDATION_ERROR: "Payroll validation failed",
        INTERNAL_ERROR: "Something went wrong",
      };
      return titled(titles[code] || fallback.title);
    }

    if (code === "PAYROLL_MODE_MISMATCH") {
      return {
        code,
        title: "Attendance settings have changed",
        message:
          "This payroll run was created with different attendance rules than your organisation uses now.",
        hint: "You need to refresh the run before salaries can be calculated.",
        steps: [
          "Click Refresh & recalculate below to clear outdated data",
          "Click Process Payroll to calculate salaries with current attendance",
        ],
        actionType: "reset",
        variant: "warning",
        guideLinks: null,
      };
    }

    if (code === "PAYROLL_ALREADY_PROCESSED") {
      return {
        code,
        title: "Payroll already calculated",
        message: "This run already has salary entries from a previous calculation.",
        hint: "Reset only if you need to recalculate with updated attendance or salary packages.",
        steps: [
          "Click Refresh & recalculate to clear previous amounts",
          "Click Process Payroll to calculate again",
        ],
        actionType: "reset",
        variant: "warning",
        guideLinks: null,
      };
    }

    if (code === "ATTENDANCE_VALIDATION_FAILED") {
      return {
        code,
        title: "Attendance needs correction",
        message: backendMsg || "Some attendance records must be fixed before payroll can run.",
        hint: "Payroll uses attendance to compute payable days and LOP.",
        steps: [
          "Open Attendance and review records for this pay period",
          "Fix missing days, unknown statuses, or validation errors",
          "Return here and click Process Payroll",
        ],
        actionType: "attendance",
        variant: "error",
        guideLinks: [{ label: "Open Attendance", to: "/attendance", primary: true }],
      };
    }

    if (code === "PAYROLL_MISSING_SALARY_PACKAGES") {
      const missing = detail.details?.employees_without_package || [];
      const futureRows = missing.filter((e) => e.reason === "future_effective");
      const noPackageRows = missing.filter((e) => e.reason !== "future_effective");
      const names = missing
        .slice(0, 5)
        .map((e) => e.employee_name || e.employee_code || e.employee_id)
        .join(", ");
      const suffix = missing.length > 5 ? ` (+${missing.length - 5} more)` : "";
      const firstId = missing[0]?.employee_id;
      const assignLink = {
        label: "Assign to Employees",
        to: firstId
          ? `/employeesalarystructure?employee_id=${encodeURIComponent(firstId)}`
          : "/employeesalarystructure",
        primary: true,
      };
      const packageGuideLinks = [assignLink, { label: "3.3 Salary Structures", to: "/salary-v2/structures" }];
      if (futureRows.length === missing.length && futureRows.length > 0) {
        return {
          code,
          title: "Salary package effective date is too late",
          message: `${futureRows.length} employee${futureRows.length === 1 ? "" : "s"} ha${futureRows.length === 1 ? "s" : "ve"} a salary package that starts after this pay period ends.`,
          hint: names ? `Affected: ${names}${suffix}` : backendHint,
          steps: [
            "Go to Assign to Employees (Employee Salary Structure)",
            "Set Effective From on or before the pay period end date",
            "Return here and click Process Payroll",
          ],
          actionType: "salary_packages",
          variant: "error",
          guideLinks: packageGuideLinks,
        };
      }
      const noPkgNames = noPackageRows
        .slice(0, 5)
        .map((e) => e.employee_name || e.employee_code || e.employee_id)
        .join(", ");
      return {
        code,
        title: "Salary packages not assigned",
        message: `${missing.length} active employee${missing.length === 1 ? "" : "s"} need a salary template and CTC valid for this pay period before payroll can run.`,
        hint: noPackageRows.length
          ? `${noPkgNames || names}${suffix} ${noPackageRows.length === 1 ? "has" : "have"} no salary package yet — changing Effective From on other employees will not fix this.`
          : names
            ? `Affected: ${names}${suffix}`
            : null,
        steps: [
          "Go to Assign to Employees",
          `Select ${noPkgNames || names || "each listed employee"} and assign a template + annual CTC`,
          "Set Effective From on or before the pay period end date",
          "Return here and click Process Payroll",
        ],
        actionType: "salary_packages",
        variant: "error",
        guideLinks: packageGuideLinks,
      };
    }

    if (code === "PAYROLL_LIFECYCLE_LOCKED") {
      return {
        code,
        title: "Payroll run is locked",
        message: backendMsg || "This payroll run is locked and cannot be changed.",
        steps: ["Contact an administrator if you need to unlock this run"],
        actionType: null,
        variant: "error",
      };
    }

    if (code === "PAYROLL_EXECUTION_IN_PROGRESS") {
      return {
        code,
        title: "Payroll is already running",
        message: "A calculation is in progress for this run. Please wait for it to finish.",
        steps: ["Wait a few moments and refresh this page", "If it stays stuck, contact support"],
        actionType: null,
        variant: "info",
      };
    }

    return {
      ...fallback,
      code,
      message: backendMsg || fallback.message,
      variant,
      guideLinks: backendLinks.length ? backendLinks : defaultPayrollGuideLinks(code, backendMsg),
    };
  }

  const text =
    typeof detail === "string"
      ? detail
      : detail?.message ||
        getApiErrorDetail(error) ||
        error?.message ||
        "Something went wrong. Please try again.";
  const lower = text.toLowerCase();

  const engineHumanized = humanizeSalaryEngineText(text);
  if (engineHumanized) return engineHumanized;

  if (lower.includes("internal server error")) {
    return {
      code: "INTERNAL_ERROR",
      title: "Payroll could not be processed",
      message:
        "The server encountered an unexpected problem while calculating payroll. Your data is safe, but this run did not complete.",
      hint: "This is usually fixed by retrying after a moment.",
      steps: [
        "Wait a few seconds and click Process Payroll again",
        "If it fails again, check Salary Templates and Statutory settings",
        "Contact your administrator if the problem continues",
      ],
      actionType: null,
      variant: "error",
    };
  }

  if (lower.includes("salary package")) {
    return {
      code: "PAYROLL_MISSING_SALARY_PACKAGES",
      title: "Salary packages not assigned",
      message: text,
      steps: [
        "Go to Assign to Employees",
        "Assign template and CTC to each employee",
        "Return and click Process Payroll",
      ],
      actionType: "salary_packages",
      variant: "error",
      guideLinks: defaultPayrollGuideLinks("PAYROLL_MISSING_SALARY_PACKAGES", text),
    };
  }

  if (lower.includes("derived variable") && (lower.includes("not defined") || lower.includes("not found"))) {
    return {
      code: "PAYROLL_DERIVED_VARIABLE",
      title: "Missing formula / derived variable",
      message: text.slice(0, 360),
      hint: "A salary component needs a formula that is not set up yet for this organisation.",
      steps: [
        "Open Derived Formulas and create the missing formula named in the error",
        "Open 3.2 Salary Components and confirm the component uses that formula",
        "Open 3.3 Salary Structures and verify the mapping",
        "Process Payroll again",
      ],
      actionType: "salary_setup",
      variant: "error",
      guideLinks: defaultPayrollGuideLinks("PAYROLL_DERIVED_VARIABLE", text),
    };
  }

  if (lower.includes("salary engine") || lower.includes("template mapping")) {
    return {
      code: "PAYROLL_SALARY_ENGINE_ERROR",
      title: "Salary calculation failed",
      message: text.slice(0, 360),
      hint: "Follow Payroll Setup in order: Settings → Components → Formulas → Structures → Assign employees.",
      steps: [
        "Open 3.1 Payroll Settings and save your pay cycle",
        "Open 3.2 Salary Components and confirm earnings/deductions exist",
        "Open Derived Formulas if a formula name is mentioned in the error",
        "Open 3.3 Salary Structures and fix mappings",
        "Process Payroll again",
      ],
      actionType: "salary_setup",
      variant: "error",
      guideLinks: defaultPayrollGuideLinks("PAYROLL_SALARY_ENGINE_ERROR", text),
    };
  }

  if (
    lower.includes("attendance settings") ||
    lower.includes("regenerate") ||
    lower.includes("attendance mode")
  ) {
    return {
      code: "PAYROLL_MODE_MISMATCH",
      title: "Attendance settings have changed",
      message: "This payroll run needs to be refreshed before you can process it.",
      steps: [
        "Click Refresh & recalculate",
        "Click Process Payroll",
      ],
      actionType: "reset",
      variant: "warning",
    };
  }

  if (lower.includes("already processed")) {
    return {
      code: "PAYROLL_ALREADY_PROCESSED",
      title: "Payroll already calculated",
      message: text,
      steps: ["Click Refresh & recalculate", "Click Process Payroll again"],
      actionType: "reset",
      variant: "warning",
    };
  }

  return {
    ...fallback,
    message: text,
  };
}

/** Plain-text message (for toasts or logs). */
export function formatPayrollProcessError(error) {
  return parsePayrollProcessError(error).message;
}

export async function fetchMonthLockStatus(monthKey) {
  const res = await API.get("/payrolls/month-lock-status", { params: { month: monthKey } });
  return res.data;
}

export async function openAttendanceCorrection(payPeriodId, reason = "") {
  const res = await API.post(`/payrolls/pay-periods/${payPeriodId}/open-correction`, {
    reason: reason || undefined,
  });
  return res.data;
}

export async function closeAttendanceCorrection(payPeriodId, { reason = "", refreshPayroll = true } = {}) {
  const res = await API.post(`/payrolls/pay-periods/${payPeriodId}/close-correction`, {
    reason: reason || undefined,
    refresh_payroll: refreshPayroll,
  });
  return res.data;
}

export function requestPayrollProcessApproval(runId, payload) {
  return API.post(`/payrolls/${runId}/process-approval`, payload || {});
}

export function rejectPayrollProcessApproval(runId, note) {
  return API.post(`/payrolls/${runId}/process-approval/reject`, { note: note || "" });
}

export function listPayrollProcessApprovals() {
  return API.get("/payrolls/process-approvals");
}

export function resetPayrollForReprocess(runId, options = {}) {
  const idempotencyKey = options.idempotencyKey ?? newIdempotencyKey();
  return API.post(
    `/payrolls/${runId}/reset-for-reprocess`,
    {},
    {
      headers: idempotencyHeaders("payroll-reset", idempotencyKey),
    }
  ).then((res) => ({ ...res, idempotencyKey }));
}
